import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Form, FormGroup } from 'semantic-ui-react';
import { useEffect, useMemo, useState } from 'react';
import { LoaderBar, SkupremeModal } from '../../components';
import {
  ControlledTextFormField,
  DropdownFormField,
  HoverBorderButton,
  MuteText,
  StyledButton,
} from '../../elements';
import { BillingService, CommonUtility, ToastMessage } from '../../utility';

const ItemSchema = yup.object().shape({
  category: yup.string().required('Category is required'),
  serviceName: yup.string().required('Service Name is required'),
  description: yup.string().required('Description is required'),
  quantity: yup.number().required('Quantity is required').typeError('Amount is required'),
});

export function AddLineItemsPopup({
  onClose,
  open,
  rateOptions,
  rateLoading,
  invoiceId,
  onAddedLineItems,
  currentLineItem,
}) {
  const isUpdate = currentLineItem?._id;
  const [loading, setLoading] = useState(false);

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(ItemSchema),
    shouldUnregister: true,
  });

  const category = watch('category');
  const service = watch('serviceName');
  const quantity = watch('quantity');

  const serviceOptions = useMemo(
    () => rateOptions.find(x => x.value === category)?.subOptions || [],
    [rateOptions, category],
  );

  const serviceOption = useMemo(() => {
    if (service) {
      return serviceOptions.find(x => x.value === service);
    }
    return {};
  }, [service, serviceOptions]);

  const amount = useMemo(
    () => (quantity || 0) * (serviceOption?.price || 0),
    [quantity, serviceOption],
  );

  const onSubmit = async formValues => {
    try {
      setLoading(true);
      const payload = {
        ...formValues,
        unit: serviceOption.unit,
        serviceName: serviceOption.name,
        pricePerUnit: serviceOption.price,
        amount,
      };

      let res = {};

      if (currentLineItem?._id) {
        res = await BillingService.updateLineItem(
          invoiceId,
          currentLineItem._id,
          payload,
        );
        ToastMessage.success('Line item updated successfully');
      } else {
        res = await BillingService.addLineItem(invoiceId, payload);
        ToastMessage.success('Line item added successfully');
      }

      onAddedLineItems(res);
      onClose();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentLineItem) {
      const category = rateOptions?.find(x => x.value === currentLineItem?.category);
      const service = category?.subOptions?.find(
        x => x.name === currentLineItem?.serviceName && currentLineItem.unit === x.unit,
      );
      reset({ ...currentLineItem, serviceName: service?.value });
    } else {
      reset({
        category: '',
        serviceName: '',
        unit: '',
        description: '',
        quantity: '',
        pricePerUnit: '',
        amount: '',
      });
    }
  }, [currentLineItem, open]);

  useEffect(() => {
    if (!category || !CommonUtility.isValidArray(rateOptions)) return;

    const categoryDetail = rateOptions.find(x => x.value === category);
    if (currentLineItem?.serviceName) {
      if (categoryDetail.subOptions.find(x => x.value === currentLineItem?.serviceName)) {
        setValue('serviceName', currentLineItem?.serviceName);
      }
    } else {
      setValue('serviceName', categoryDetail?.subOptions?.[0]?.value);
    }
  }, [category, currentLineItem, rateOptions]);

  return (
    <SkupremeModal
      title={isUpdate ? 'Update Line Item' : 'Add Line Item'}
      open={open}
      size="tiny"
      onClose={onClose}
    >
      <Form onSubmit={handleSubmit(onSubmit)} className="position-relative">
        {rateLoading && <LoaderBar />}
        <FormGroup widths="equal">
          <DropdownFormField
            control={control}
            name="category"
            error={errors?.category}
            hint={errors?.category?.message}
            options={rateOptions}
            label="Category"
          />
          <DropdownFormField
            control={control}
            name="serviceName"
            error={errors?.serviceName}
            hint={errors?.serviceName?.message}
            options={serviceOptions}
            label="Service Name"
          />
        </FormGroup>
        <FormGroup widths="equal">
          <ControlledTextFormField
            label="Description"
            control={control}
            name="description"
            error={errors?.description}
            hint={errors?.description?.message}
          />
          <ControlledTextFormField
            control={control}
            label="Quantity"
            name="quantity"
            error={errors?.quantity}
            hint={errors?.quantity?.message}
          />
        </FormGroup>
        <div className="d-flex justify-content-between mb-4">
          <div className="d-flex flex-column mx-2">
            <MuteText className="text-nowrap">Unit Type</MuteText>
            <span className="text-nowrap">{serviceOption?.unit || 'Select Service'}</span>
          </div>
          <div className="d-flex flex-column mx-2">
            <MuteText className="text-nowrap">Price per unit</MuteText>
            <span className="text-nowrap">
              {CommonUtility.currencyFormat(serviceOption?.price)}
            </span>
          </div>
          <div className="d-flex flex-column mx-2">
            <MuteText className="text-nowrap">Amount</MuteText>
            <span className="text-nowrap">{CommonUtility.currencyFormat(amount)}</span>
          </div>
        </div>
        <div className="d-flex justify-content-end">
          <HoverBorderButton disabled={loading} onClick={onClose} type="button">
            Cancel
          </HoverBorderButton>
          <StyledButton loading={loading} type="submit" className="ml-2">
            {isUpdate ? 'Update' : 'Create'}
          </StyledButton>
        </div>
      </Form>
    </SkupremeModal>
  );
}
